import { useEffect, useState } from 'react';
import { reviews as reviewsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

export default function ReviewSection({ storeId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => reviewsApi.list(storeId).then(setItems).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [storeId]);

  const submit = async () => {
    if (!user) { toast.error('Please sign in to leave a review'); return; }
    setBusy(true);
    try {
      await reviewsApi.add(storeId, { rating, comment });
      setComment(''); setRating(5);
      toast.success('Thanks for your review!');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post review');
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="reviews-section" className="card-soft p-5 mt-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Customer reviews</div>
          <h3 className="font-heading font-bold text-xl mt-1">{items.length} review{items.length !== 1 ? 's' : ''}</h3>
        </div>
      </div>

      {user && (
        <div className="mt-4 rounded-lg border border-border bg-white p-4">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-2">Your rating</div>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)} className="p-1">
                <Star className={`w-6 h-6 ${rating >= n ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/30'}`}/>
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" value={comment} onChange={e=>setComment(e.target.value)} rows={2} placeholder="Tell other villagers about your experience…" className="w-full mt-3 border border-border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20"/>
          <button data-testid="submit-review" disabled={busy} onClick={submit} className="btn-primary mt-3">{busy ? 'Posting…' : 'Post review'}</button>
          <div className="text-[10px] text-ink/40 mt-2 uppercase tracking-wider">Only customers with a delivered order can post a review.</div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {items.length === 0 ? <div className="text-sm text-ink/60">No reviews yet. Be the first!</div>
          : items.map(r => (
            <div key={r.id} data-testid={`review-${r.id}`} className="rounded-lg border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{r.user_name}</div>
                <div className="inline-flex items-center gap-0.5">
                  {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${r.rating >= n ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/20'}`}/>)}
                </div>
              </div>
              {r.comment && <div className="text-sm text-ink/80 mt-1.5">{r.comment}</div>}
              <div className="text-[10px] text-ink/40 mt-1 uppercase tracking-wider">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
          ))
        }
      </div>
    </section>
  );
}
